import React, { useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Download } from 'lucide-react';

// Set up the worker for PDF.js using jsDelivr CDN (serves with correct MIME type)
// Version must match the pdfjs-dist version used by react-pdf (5.4.296)
pdfjs.GlobalWorkerOptions.workerSrc = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@5.4.296/build/pdf.worker.min.mjs';

const PDFViewer = ({ pdfBase64, isDarkMode, documentTitle = 'document' }) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);

  // Convert base64 to Uint8Array for react-pdf (more reliable than data URL)
  const pdfData = React.useMemo(() => {
    if (!pdfBase64) return null;
    try {
      // Decode base64 to binary string
      const binaryString = atob(pdfBase64);
      // Convert binary string to Uint8Array
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      console.log('PDF Uint8Array created, length:', bytes.length);
      return { data: bytes };
    } catch (error) {
      console.error('Error converting base64 to Uint8Array:', error);
      return null;
    }
  }, [pdfBase64]);

  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
    setPageNumber(1);
    console.log('PDF loaded successfully with', numPages, 'pages');
  }

  function onDocumentLoadError(error) {
    console.error('PDF load error:', error);
  }

  function changePage(offset) {
    setPageNumber(prevPageNumber => prevPageNumber + offset);
  }

  function previousPage() {
    changePage(-1);
  }

  function nextPage() {
    changePage(1);
  }

  function zoomIn() {
    setScale(prevScale => Math.min(prevScale + 0.2, 2.0));
  }

  function zoomOut() {
    setScale(prevScale => Math.max(prevScale - 0.2, 0.5));
  }

  function downloadPDF() {
    try {
      // Convert base64 to blob
      const binaryString = atob(pdfBase64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: 'application/pdf' });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${documentTitle.replace(/[^a-z0-9]/gi, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('PDF download initiated');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Error al descargar el PDF');
    }
  }

  // Reusable controls component
  const Controls = () => (
    <div className={`flex flex-col sm:flex-row items-center justify-between gap-4 p-4 rounded-lg ${
      isDarkMode ? 'bg-gray-700' : 'bg-gray-100'
    }`}>
      {/* Page Navigation */}
      <div className="flex items-center space-x-2">
        <button
          onClick={previousPage}
          disabled={pageNumber <= 1}
          className={`p-2 rounded-lg transition-all duration-300 ${
            pageNumber <= 1
              ? 'opacity-50 cursor-not-allowed'
              : isDarkMode
              ? 'bg-gray-600 hover:bg-gray-500'
              : 'bg-gray-200 hover:bg-gray-300'
          }`}
          aria-label="Página anterior"
        >
          <ChevronLeft size={20} />
        </button>
        <span className="text-sm font-semibold whitespace-nowrap">
          Página {pageNumber} de {numPages || '--'}
        </span>
        <button
          onClick={nextPage}
          disabled={pageNumber >= numPages}
          className={`p-2 rounded-lg transition-all duration-300 ${
            pageNumber >= numPages
              ? 'opacity-50 cursor-not-allowed'
              : isDarkMode
              ? 'bg-gray-600 hover:bg-gray-500'
              : 'bg-gray-200 hover:bg-gray-300'
          }`}
          aria-label="Página siguiente"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Zoom Controls */}
      <div className="flex items-center space-x-2">
        <button
          onClick={zoomOut}
          disabled={scale <= 0.5}
          className={`p-2 rounded-lg transition-all duration-300 ${
            scale <= 0.5
              ? 'opacity-50 cursor-not-allowed'
              : isDarkMode
              ? 'bg-gray-600 hover:bg-gray-500'
              : 'bg-gray-200 hover:bg-gray-300'
          }`}
          aria-label="Alejar"
        >
          <ZoomOut size={20} />
        </button>
        <span className="text-sm font-semibold whitespace-nowrap">{Math.round(scale * 100)}%</span>
        <button
          onClick={zoomIn}
          disabled={scale >= 2.0}
          className={`p-2 rounded-lg transition-all duration-300 ${
            scale >= 2.0
              ? 'opacity-50 cursor-not-allowed'
              : isDarkMode
              ? 'bg-gray-600 hover:bg-gray-500'
              : 'bg-gray-200 hover:bg-gray-300'
          }`}
          aria-label="Acercar"
        >
          <ZoomIn size={20} />
        </button>
      </div>

      {/* Download Button */}
      <button
        onClick={downloadPDF}
        className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-semibold transition-all duration-300 ${
          isDarkMode 
            ? 'bg-purple-700 hover:bg-purple-600 text-white' 
            : 'bg-purple-600 hover:bg-purple-700 text-white'
        }`}
        aria-label="Descargar PDF"
      >
        <Download size={20} />
        <span className="hidden sm:inline">Descargar</span>
      </button>
    </div>
  );

  return (
    <div className="pdf-viewer-container">
      {/* Top Controls */}
      <div className="mb-4">
        <Controls />
      </div>

      {/* PDF Document */}
      <div className={`flex justify-center overflow-auto p-4 rounded-lg ${
        isDarkMode ? 'bg-gray-800' : 'bg-gray-50'
      }`}>
        <Document
          file={pdfData}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={
            <div className="text-center py-12">
              <div className={`animate-spin rounded-full h-12 w-12 border-b-2 mx-auto ${
                isDarkMode ? 'border-purple-400' : 'border-purple-600'
              }`}></div>
              <p className="mt-4">Cargando PDF...</p>
            </div>
          }
          error={
            <div className={`text-center py-12 ${
              isDarkMode ? 'text-red-400' : 'text-red-600'
            }`}>
              <p>Error al cargar el PDF</p>
            </div>
          }
        >
          <Page
            pageNumber={pageNumber}
            scale={scale}
            className="shadow-lg"
          />
        </Document>
      </div>

      {/* Bottom Controls */}
      <div className="mt-4">
        <Controls />
      </div>
    </div>
  );
};

export default PDFViewer;
